/**
 * Network guards shared by both servers (pattern from Mantle Watch): private listeners
 * bind only to a tailnet address and accept only tailnet peers.
 */
export const TAILSCALE_IPV4_CIDR = '100.64.0.0/10';
export const TAILSCALE_IPV6_PREFIX = 'fd7a:115c:a1e0:';

/** Default listener ports; the gateway↔school contract is in docs/CONTRACTS.md. */
export const PORTS = {
  tenantPublic: 3000,
  tenantPrivate: 3001,
  centralPublic: 4000,
  centralPrivate: 4001,
  schoolCaddyTls: 443,
} as const;

const WILDCARD_HOSTS = new Set(['0.0.0.0', '::', '[::]', '']);

function ipv4ToInt(ip: string): number | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  const nums = parts.map((p) => (/^\d{1,3}$/.test(p) ? Number(p) : NaN));
  if (nums.some((n) => Number.isNaN(n) || n > 255)) return null;
  return nums.reduce((acc, n) => acc * 256 + n, 0);
}

const HEX_GROUP = /^[0-9a-f]{1,4}$/;

/** "a.b.c.d" → two hex groups, or null. */
function ipv4ToGroups(ip: string): number[] | null {
  const value = ipv4ToInt(ip);
  return value === null ? null : [Math.floor(value / 65536), value % 65536];
}

/** The groups on one side of `::` (dotted IPv4 allowed last), or null. */
function parseGroups(part: string, allowIpv4Tail: boolean): number[] | null {
  if (part === '') return [];
  const pieces = part.split(':');
  const last = pieces.at(-1) ?? '';
  const tail = allowIpv4Tail && last.includes('.') ? ipv4ToGroups(last) : [];
  const hex = tail !== null && last.includes('.') ? pieces.slice(0, -1) : pieces;
  if (tail === null || !hex.every((g) => HEX_GROUP.test(g))) return null;
  return [...hex.map((g) => parseInt(g, 16)), ...tail];
}

/** The eight 16-bit groups of an IPv6 address, or null when it is not one. */
function ipv6Groups(address: string): number[] | null {
  const halves = address.toLowerCase().split('::');
  if (halves.length > 2) return null;
  const head = parseGroups(halves[0] ?? '', halves.length === 1);
  const tail = halves.length === 2 ? parseGroups(halves[1] ?? '', true) : [];
  if (!head || !tail) return null;
  const missing = 8 - head.length - tail.length;
  if (halves.length === 1 ? missing !== 0 : missing < 1) return null;
  return [...head, ...Array<number>(missing).fill(0), ...tail];
}

/** Start and length of the longest run (≥ 2) of zero groups; leftmost wins a tie. */
function longestZeroRun(groups: readonly number[]): { start: number; length: number } {
  let best = { start: -1, length: 0 };
  let start = -1;
  groups.forEach((group, i) => {
    if (group !== 0) {
      start = -1;
      return;
    }
    if (start === -1) start = i;
    const length = i - start + 1;
    if (length > best.length) best = { start, length };
  });
  return best.length >= 2 ? best : { start: -1, length: 0 };
}

/**
 * RFC 5952 text form of an IPv6 address: lowercase, no leading zeros, the longest run of two
 * or more zero groups as `::`. One spelling per address, so a UNIQUE column holds (review N7).
 * Null when the input is not an IPv6 address.
 */
export function canonicalIpv6(address: string): string | null {
  const groups = ipv6Groups(address);
  if (!groups) return null;
  const hex = groups.map((g) => g.toString(16));
  const run = longestZeroRun(groups);
  if (run.start === -1) return hex.join(':');
  const head = hex.slice(0, run.start).join(':');
  const tail = hex.slice(run.start + run.length).join(':');
  return `${head}::${tail}`;
}

/** True for 100.64.0.0/10, fd7a:115c:a1e0::/48 and IPv4-mapped forms of them. */
export function isTailnetAddress(address: string): boolean {
  const ip = address.toLowerCase().replace(/^::ffff:/, '');
  if (ip.startsWith(TAILSCALE_IPV6_PREFIX)) return true;
  const value = ipv4ToInt(ip);
  if (value === null) return false;
  const base = 100 * 2 ** 24 + 64 * 2 ** 16;
  return value >= base && value < base + 2 ** 22;
}

export function isLoopbackAddress(address: string): boolean {
  const ip = address.toLowerCase().replace(/^::ffff:/, '');
  return ip === '::1' || ip.startsWith('127.');
}

/** Refuses wildcard and non-tailnet binds for private listeners; dev may allow loopback. */
export function assertPrivateBindHost(host: string, options: { allowLoopback: boolean }): void {
  if (WILDCARD_HOSTS.has(host)) {
    throw new Error(`Private listener must not bind to "${host}"; use the Tailscale IP`);
  }
  if (isTailnetAddress(host)) return;
  if (options.allowLoopback && isLoopbackAddress(host)) return;
  throw new Error(`Private listener host ${host} is not a Tailscale address`);
}
